import { Box2, Vector2 } from "three";
import { HTMLColor } from "../../colors/HTMLColor";
import { ElementType } from "../../dom-tools/ElementType";
import { GraphicalElement } from "./GraphicalElement";

export class Canvas2dBase extends GraphicalElement
{  
  #canvas:HTMLCanvasElement;
  #context:CanvasRenderingContext2D;

  get canvas()
  {
    return this.#canvas;
  }

  get context()
  {
    return this.#context
  }

  constructor( element:Element )
  {
    super();

    this._setElement( element );
    this.#canvas = ElementType.canvas.create();
    this.#context = this.#canvas.getContext( "2d" );

    this.element.appendChild( this.#canvas );
    
    this.dimensions.onChange.addListener(
      ()=>
      {
        this.#canvas.width = this.width;
        this.#canvas.height = this.height;
      }
    )
  }

  protected clear()
  {
    this.context.clearRect( 0, 0, this.width, this.height );
  }

  protected clearBox( b: Box2 )
  {
    this.context.clearRect( b.min.x, b.min.y, b.max.x - b.min.x, b.max.y - b.min.y );
  }

  protected fillBackground( color:HTMLColor )
  {
    this.context.fillStyle = color + "";
    this.context.fillRect( 0, 0, this.width, this.height );
  }

  protected rangeTransform( start:Vector2, end:Vector2 )
  {
    this.context.translate( -start.x, -start.y );
    this.context.scale( end.x - start.x , end.y - start.y );
  }

  protected push()
  {
    this.context.save();
  }

  protected pop()
  {
    this.context.restore();
  }

  protected setLineStyle( color:HTMLColor, lineWidth:number=undefined, 
                          lineCap:CanvasLineCap=undefined, lineJoin:CanvasLineJoin=undefined  )
  {
    this.#context.strokeStyle = color + "";

    if ( lineWidth !== undefined )
    {
      this.#context.lineWidth = lineWidth;
    }

    if ( lineCap !== undefined )
    {
      this.#context.lineCap = lineCap;
    }

    if ( lineJoin !== undefined )
    {
      this.#context.lineJoin = lineJoin;
    }
    
  }

  protected drawLine( start:Vector2, end:Vector2 )
  {
    this.context.beginPath();
    this.context.moveTo( start.x, start.y );
    this.context.lineTo( end.x, end.y );
    this.context.stroke();
  }

  protected drawLineBox( start:Vector2, end:Vector2 )
  {
    this.context.beginPath();
    this.context.moveTo( start.x, start.y );
    this.context.lineTo( end.x, start.y );
    this.context.lineTo( end.x, end.y );
    this.context.lineTo( start.x, end.y );
    this.context.closePath();

    this.context.stroke();
  }

  protected drawBezier( a:Vector2, b:Vector2, c:Vector2, d:Vector2 )
  {
    this.context.beginPath();
    this.context.moveTo( a.x, a.y );
    this.context.bezierCurveTo( b.x, b.y, c.x, c.y, d.x, d.y );
    this.context.stroke();
  }

  protected drawVerticalLines( offset:number, length:number, distance:number, minY:number, maxY:number )
  {
    this.context.beginPath();

    for ( let i = 0; i < length; i++ )
    {
      let x = i * distance + offset;
      this.context.moveTo( x, minY );
      this.context.lineTo( x, maxY );
    }

    this.context.stroke();
  }

  protected drawHorizontalLines( offset:number, length:number, distance:number, minX:number, maxX:number )
  {
    this.context.beginPath();

    for ( let i = 0; i < length; i++ )
    {
      let y = i * distance + offset;
      this.context.moveTo( minX, y );
      this.context.lineTo( maxX, y);
    }

    this.context.stroke();
  }

  protected drawLines( list:Vector2[] )
  {
    this.context.beginPath();

    for( let i = 0; i < list.length; i+=2 )
    {
      let start = list[ i ];
      let end   = list[ i + 1 ];

      this.context.moveTo( start.x, start.y );
      this.context.lineTo( end.x, end.y );
    }

    this.context.stroke();
  }
}